/**
 * Active Subscription view — SaaS-style billing dashboard.
 * Single main area: Pro Plan card (activation/expiry, billing cycle), overview stats, usage metrics, plan benefits.
 * Display-only UI; no logic/API changes.
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, LinearProgress, Tooltip, Alert } from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  People as PeopleIcon,
  InfoOutlined as InfoIcon,
  CheckCircle as CheckIcon,
  AutoAwesome as SparkleIcon,
  EventAvailable as ActivationIcon,
  EventBusy as ExpiryIcon,
  Schedule as BillingCycleIcon,
  FiberManualRecord as ActiveDotIcon,
} from '@mui/icons-material';

const VOICE_CREDITS_INFO = 'Cost for AI insights from voice call recording, per minute.';

// Design tokens: deep teal primary, soft neutrals
const teal = {
  50: '#f0fdfa',
  100: '#ccfbf1',
  200: '#99f6e4',
  500: '#14b8a6',
  600: '#0d9488',
  700: '#0f766e',
  800: '#115e59',
  900: '#134e4a',
};
const neutral = {
  50: '#fafafa',
  100: '#f4f4f5',
  200: '#e4e4e7',
  300: '#d4d4d8',
  400: '#a1a1aa',
  500: '#71717a',
  600: '#52525b',
  700: '#3f3f46',
  800: '#27272a',
  900: '#18181b',
};

function AnimatedProgress({ value, ...rest }) {
  const [displayValue, setDisplayValue] = useState(0);
  const clamped = Math.min(100, Math.max(0, Number(value) || 0));

  useEffect(() => {
    const t = setTimeout(() => setDisplayValue(clamped), 50);
    return () => clearTimeout(t);
  }, [clamped]);

  return (
    <LinearProgress
      variant="determinate"
      value={displayValue}
      {...rest}
      sx={{
        height: 8,
        borderRadius: 1,
        bgcolor: neutral[200],
        '& .MuiLinearProgress-bar': {
          borderRadius: 1,
          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        ...rest.sx,
      }}
    />
  );
}

export default function SubscriptionOverview({
  subscriptionData,
  userSlotUsage,
  creditsUsage,
  hasVoiceCredits = true,
}) {
  console.log('subscription overview subscriptionData:', subscriptionData);
  // Extract data from subscriptionData
  const plan = subscriptionData?.plan ?? {};
  console.log('subscription overview plan:', plan);
  const isPlanActive = plan.status === 'active';
  const users = subscriptionData?.users ?? {};
  const credits = subscriptionData?.credits ?? {};
  const pricing = subscriptionData?.pricing ?? {};
  const billing = subscriptionData?.billing ?? {};

  // Display status
  const isExpired = typeof plan.days_remaining === 'number' && plan.days_remaining <= 0;
  const displayStatus = isExpired ? 'expired' : plan.status ?? 'active';
  const displayStatusVerbose = isExpired ? 'Expired' : (plan.status_verbose ?? 'Active');

  // Plan name
  const planDisplayName = () => {
    const name = plan.name || 'Free Trial';
    const days = plan.days_remaining;
    const planType = plan.plan_type;
    if (planType === 'free' && typeof days === 'number') {
      return `${name} · ${days} days`;
    }
    return name;
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Date texts
  const activationDateText = formatDate(plan.start_date);
  const expiryDateText = formatDate(plan.end_date);
  const billingCycleLabel = billing.cycle_display ?? '—';

  // Pricing
  const perUserCostDisplay = pricing.discounted?.per_user_cost ?? pricing.per_user_cost ?? 1200;
  const perCreditCostDisplay = pricing.discounted?.per_credit_cost ?? pricing.per_credit_cost ?? 0;

  // Benefits
  const proPlanBenefits = pricing.pro_benefits ?? [];

  // Alerts: show backend alert when non-null/non-empty; else front-end "running out" message
  const hasBackendUserAlert = users.alert != null && String(users.alert).trim() !== '';
  const userSeatsRemaining = (userSlotUsage?.total ?? 0) - (userSlotUsage?.used ?? 0);
  const userSlotAlert = hasBackendUserAlert
    ? users.alert
    : (userSlotUsage?.total > 0 && userSeatsRemaining <= 1
        ? 'You are running out of user seats. Please add more user seats to add new users.'
        : null);

  // Voice credits balance (wallet-like: no progress bar; show available)
  const voiceCreditsBalance =
    credits.available ??
    (typeof credits.total === 'number' && typeof credits.used === 'number'
      ? credits.total - credits.used
      : null) ??
    creditsUsage?.remaining ??
    0;
  const creditsAvailable = Number(voiceCreditsBalance);
  const hasBackendCreditsAlert = credits.alert != null && String(credits.alert).trim() !== '';
  const creditsAlert = hasBackendCreditsAlert
    ? credits.alert
    : (hasVoiceCredits && creditsAvailable < 500
        ? 'You are running out of voice credits. Please add more credits to continue AI call analysis.'
        : null);

  return (
    <Box>
      {/* Billing Overview — Pro Plan card: green active dot above Pro, dates without boxes */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: neutral[200],
          bgcolor: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          transition: 'box-shadow 0.2s ease',
          '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
          {/* Pro badge with green active indicator above */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
            {isPlanActive && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ActiveDotIcon sx={{ fontSize: 10, color: teal[500] }} />
                <Typography variant="caption" fontFamily="'Plus Jakarta Sans', sans-serif" sx={{ color: teal[700], fontWeight: 600 }}>
                  Active
                </Typography>
              </Box>
            )}
            <Typography
              component="span"
              variant="body1"
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                bgcolor: teal[100],
                color: teal[800],
                fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                letterSpacing: '0.02em',
              }}
            >
              {planDisplayName()}
            </Typography>
          </Box>

          {/* Activation, Expiry, Billing cycle — icons + text, no box outline */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 2, sm: 3.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ActivationIcon sx={{ fontSize: 20, color: teal[600] }} />
              <Box>
                <Typography variant="caption" color="text.secondary" fontFamily="'Plus Jakarta Sans', sans-serif" sx={{ display: 'block', lineHeight: 1.2 }}>Activation</Typography>
                <Typography variant="body2" fontWeight={600} fontFamily="'Plus Jakarta Sans', sans-serif">{activationDateText}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExpiryIcon sx={{ fontSize: 20, color: teal[600] }} />
              <Box>
                <Typography variant="caption" color="text.secondary" fontFamily="'Plus Jakarta Sans', sans-serif" sx={{ display: 'block', lineHeight: 1.2 }}>Expiry</Typography>
                <Typography variant="body2" fontWeight={600} fontFamily="'Plus Jakarta Sans', sans-serif">{expiryDateText}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BillingCycleIcon sx={{ fontSize: 20, color: teal[600] }} />
              <Box>
                <Typography variant="caption" color="text.secondary" fontFamily="'Plus Jakarta Sans', sans-serif" sx={{ display: 'block', lineHeight: 1.2 }}>Billing cycle</Typography>
                <Typography variant="body2" fontWeight={600} fontFamily="'Plus Jakarta Sans', sans-serif">{billingCycleLabel}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
        {/* Usage metrics — two cards, always visible, no accordion */}
        <Typography variant="subtitle1" fontWeight={700} fontFamily="'Plus Jakarta Sans', sans-serif" sx={{ mb: 1.5, color: neutral[900] }}>
          Usage
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Limits and spendings state
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Active Users metric card */}
          <Grid item xs={12} md={hasVoiceCredits ? 6 : 12}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: neutral[200],
                  bgcolor: '#fff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    borderColor: neutral[300],
                  },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <PeopleIcon sx={{ fontSize: 22, color: teal[600] }} />
                <Typography variant="subtitle2" fontWeight={600} fontFamily="'Plus Jakarta Sans', sans-serif">
                  Active users
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Used {userSlotUsage.used} of {userSlotUsage.total}
              </Typography>
              <AnimatedProgress
                value={Math.min(100, userSlotUsage.percentage)}
                sx={{ '& .MuiLinearProgress-bar': { bgcolor: teal[500] } }}
              />
              {perUserCostDisplay != null && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: neutral[100],
                  }}
                >
                  <Typography variant="caption" color="text.warning" fontFamily="'Plus Jakarta Sans', sans-serif">
                    Per Additional User Cost: ₹{Number(perUserCostDisplay).toLocaleString('en-IN') + '/Month'}
                  </Typography>
                </Box>
              )}
              {userSlotAlert && (
                <Alert severity="warning" sx={{ mt: 2, py: 0.5, '& .MuiAlert-message': { fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.8125rem' } }}>
                  {userSlotAlert}
                </Alert>
              )}
            </Paper>
          </Grid>

          {/* Voice Credits — balance card (no progress; credits are wallet-like) */}
          {hasVoiceCredits && (
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: neutral[200],
                  bgcolor: '#fff',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    borderColor: neutral[300],
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                  <WalletIcon sx={{ fontSize: 22, color: teal[600] }} />
                  <Typography variant="subtitle2" fontWeight={600} fontFamily="'Plus Jakarta Sans', sans-serif">
                    Voice credits
                  </Typography>
                  <Tooltip title={VOICE_CREDITS_INFO} placement="top">
                    <Box component="span" sx={{ display: 'inline-flex', cursor: 'help' }}>
                      <InfoIcon sx={{ fontSize: 16, color: neutral[400] }} />
                    </Box>
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Available voice credits to analysis your calls
                </Typography>
                <Typography variant="h4" fontWeight={700} fontFamily="'Plus Jakarta Sans', sans-serif" sx={{ color: teal[700], lineHeight: 1.2 }}>
                  {Number(voiceCreditsBalance).toLocaleString('en-IN')}
                </Typography>
                {/* Two info cards: Per credit cost + 1 credit = 1 minute (two lines, compact) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                  {perCreditCostDisplay != null && (
                    <Paper
                      elevation={0}
                      sx={{
                        width: '100%',
                        py: 0.75,
                        px: 1.25,
                        borderRadius: 1.25,
                        border: '1px solid',
                        borderColor: neutral[200],
                        bgcolor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minHeight: 32,
                        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.05)', borderColor: neutral[300] },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: teal[600],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />
                      </Box>
                      <Typography variant="caption" fontWeight={600} fontFamily="'Plus Jakarta Sans', sans-serif" color={neutral[900]} sx={{ fontSize: '0.8125rem' }}>
                        Per Additional Credit Cost: ₹{Number(perCreditCostDisplay).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Paper>
                  )}
                  <Paper
                    elevation={0}
                    sx={{
                      width: '100%',
                      py: 0.75,
                      px: 1.25,
                      borderRadius: 1.25,
                      border: '1px solid',
                      borderColor: neutral[200],
                      bgcolor: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minHeight: 32,
                      transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.05)', borderColor: neutral[300] },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                      <SparkleIcon sx={{ fontSize: 18, color: '#eab308' }} />
                      <SparkleIcon sx={{ fontSize: 14, color: '#facc15' }} />
                    </Box>
                    <Typography variant="caption" fontWeight={600} fontFamily="'Plus Jakarta Sans', sans-serif" color={neutral[900]} sx={{ fontSize: '0.8125rem' }}>
                      1 credit = 1 minute of AI Minute analysis.
                    </Typography>
                  </Paper>
                </Box>
                {creditsAlert && (
                  <Alert severity="warning" sx={{ mt: 2, py: 0.5, '& .MuiAlert-message': { fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.8125rem' } }}>
                    {creditsAlert}
                  </Alert>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>

       

        {/* Included with Pro — grid of capsules (match upgrade/pro feature style) */}
        {proPlanBenefits.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mt: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: neutral[200],
              bgcolor: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} fontFamily="'Plus Jakarta Sans', sans-serif" sx={{ mb: 0.5, color: neutral[900] }}>
              Included with Pro
            </Typography>
            <Typography variant="body2" color="text.secondary" fontFamily="'Plus Jakarta Sans', sans-serif" sx={{ mb: 2 }}>
              Flexible, transparent pricing that scales with your growth.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
              {proPlanBenefits.map((item, idx) => (
                <Paper
                  key={idx}
                  elevation={0}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.75,
                    px: 1.25,
                    borderRadius: 1.25,
                    minHeight: 32,
                    bgcolor: '#fff',
                    border: '1px solid',
                    borderColor: neutral[200],
                    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                    '&:hover': {
                      borderColor: neutral[300],
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    },
                  }}
                >
                  {item.important ? (
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: teal[600],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                      <SparkleIcon sx={{ fontSize: 18, color: '#eab308' }} />
                      <SparkleIcon sx={{ fontSize: 14, color: '#facc15' }} />
                    </Box>
                  )}
                  <Typography
                    variant="caption"
                    fontFamily="'Plus Jakarta Sans', sans-serif"
                    sx={{
                      color: neutral[900],
                      fontWeight: item.important ? 600 : 500,
                      fontSize: '0.8125rem',
                    }}
                  >
                    {item.text}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Paper>
        )}
    </Box>
  );
}
